import React, { useEffect, useState } from 'react';
import { Box, Paper, Checkbox, FormControlLabel, Button, useTheme, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateJobTitle } from '../../hooks/jobTitles/useCreateJobTitle';
import { useUpdateJobTitle } from '../../hooks/jobTitles/useUpdateJobTitle';
import { useGetDepartments } from '../../hooks/departments/useGetDepartments';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';

const FormAddEditJobTitle = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const isDark = theme.palette.mode === 'dark';

    const { createJobTitle, isPending: isCreating } = useCreateJobTitle();
    const { editJobTitle, isUpdate } = useUpdateJobTitle();
    const { isLoading: deptsLoading, departments } = useGetDepartments(1, 999);

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        department_id: '',
        code: '',
        name: '',
        is_active: true,
    });

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            department_id: currentData.department_id || '',
            code: currentData.code || '',
            name: currentData.name || '',
            is_active: currentData.is_active ?? true,
        });
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setForm((prev) => ({ ...prev, [name]: checked }));
            return;
        }
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            department_id: Number(form.department_id),
            code: form.code || null,
            name: form.name,
            is_active: !!form.is_active,
        };

        if (isEdit && currentData?.id) {
            editJobTitle({ req: payload, id: currentData.id });
        } else {
            createJobTitle(payload);
        }
    };

    if (deptsLoading) return <LoadingPage />;

    const deptOptions = (departments || []).map((d) => ({ id: d.id, name: d.name }));

    return (
        <Paper sx={{ p: 3, mt: 2, direction: isArabic ? 'rtl' : 'ltr', backgroundColor: isDark && 'transparent' }}>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'القسم' : 'Department'}
                            type="select" name="department_id"
                            value={form.department_id}
                            onChange={(e) => setForm((prev) => ({ ...prev, department_id: e.target.value }))}
                            options={deptOptions}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'كود المسمى' : 'Job Title Code'}
                            placeholder={isArabic ? 'مثال: JT-01' : 'e.g. JT-01'}
                            type="text" name="code" value={form.code}
                            onChange={handleChange} required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'المسمى الوظيفي' : 'Job Title Name'}
                            placeholder={isArabic ? 'ادخل المسمى الوظيفي' : 'Enter job title'}
                            type="text" name="name" value={form.name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: { xs: 1, md: 1 } }}>
                            <FormControlLabel
                                control={<Checkbox checked={form.is_active} name="is_active" onChange={handleChange} size="small" />}
                                label={isArabic ? 'مفعّل' : 'Active'}
                            />
                        </Box>
                    </Grid>
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button type="submit" variant="contained" color="primary" disabled={loadingSubmit}>
                        {loadingSubmit
                            ? isArabic ? 'جاري الحفظ...' : 'Saving...'
                            : isEdit
                                ? isArabic ? 'تحديث' : 'Update'
                                : isArabic ? 'حفظ' : 'Save'}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditJobTitle;
